
import {
  defineComponent,
  onBeforeMount,
  ref,
  computed,
  Ref,
  ComputedRef,
  watch
} from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import Row from "../components/application/data-table/Row.vue";
import Cell from "../components/application/data-table/Cell.vue";
import TableHeader from "../components/application/data-table/TableHeader.vue";
import OrderTableHeader from "../components/application/data-table/OrderTableHeader.vue";
import Pagination from "../components/application/shop/PaginationDefault.vue";
import { Statement } from "../../../domain/entities/statement";
import {
  StatementType,
  StatementTypeCanceled,
  TransactionType
} from "../../../domain/enums/statement";
import { User } from "../../../domain/entities/entities";
import { dispatchError } from "../utils/error_handling";
import { getOrderFromURL } from "../utils/order_utils";
import { wrapLoading } from "../utils/wrap_loading";
import { updateQuery } from "../../repository/utils/routing_utils";
import DateInput from "../components/application/inputs/DateInput.vue";
import { dateGrouped, formatDateTime } from "../components/mixins";
import ContentFallback from "../components/application/moldure/ContentFallback.vue";

export default defineComponent({
  name: "StatementTable",
  methods: { formatDateTime, dateGrouped, updateQuery },
  computed: {
    StatementTypeCanceled() {
      return StatementTypeCanceled;
    }
  },
  components: {
    ContentFallback,
    DateInput,
    Row,
    Cell,
    TableHeader,
    OrderTableHeader,
    Pagination
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const statements = computed(
      () => store.getters["statement_module/statements"]
    );
    const selectedType = ref("0");
    const selectedTransaction = ref("0");
    const openedIndexes = ref<Set<number>>(new Set());

    const firstDate: Ref<string> = ref("");
    const lastDate: Ref<string> = ref("");

    const numberOfPages: ComputedRef<number> = computed(
      () => store.getters["statement_module/numberOfPagesstatements"]
    );

    const user: ComputedRef<User> = computed(
      () => store.getters["userProfile_module/user"]
    );

    const contentTypes = [
      { label: "Treinamento", value: String(StatementType.COURSE) },
      { label: "Evento", value: String(StatementType.EVENT) },
      { label: "Pílula", value: String(StatementType.PILL) },
      { label: "Acesso", value: String(StatementType.ACCESS) },
      { label: "Compra", value: String(StatementType.PRODUCT) }
    ];

    const transactionTypes = [
      { label: "Crédito", value: 1 },
      { label: "Débito", value: 2 }
    ];

    onBeforeMount(async () => {
      try {
        await loadStatements();
        await loadUser();
      } catch (e) {
        await dispatchError(e);
      }
    });

    const loadStatements = async () => {
      try {
        await wrapLoading(async () => {
          const order = getOrderFromURL(route);

          await store.dispatch("statement_module/loadStatements", { order });
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    const loadUser = async () => {
      try {
        await wrapLoading(async () => {
          await store.dispatch("userProfile_module/loadUser");
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    function formattedTransaction(
      type: TransactionType,
      isCanceled: StatementTypeCanceled
    ): string {
      if (isCanceled === StatementTypeCanceled.CANCELED) {
        return "cancelado";
      }
      if (type === TransactionType.CREDIT) {
        return "crédito";
      }
      if (type === TransactionType.DEBIT) {
        return "débito";
      }
      return "-";
    }

    const formatDecoins = (d: number) => Intl.NumberFormat("pt-br").format(d);
    const formatName = (statement: Statement) => {
      switch (statement.type) {
        case StatementType.COURSE:
          return `${statement.name}`;
        case StatementType.PRODUCT:
          return `#${statement.order.id}`;
        default:
          return `${statement.name}`;
      }
    };

    const formatStatementType = (t: StatementType) => {
      switch (t) {
        case StatementType.COURSE:
          return "Treinamento";
        case StatementType.PRODUCT:
          return "Produto";
        case StatementType.PILL:
          return "Pílula";
        case StatementType.ACCESS:
          return "Acesso";
        case StatementType.EVENT:
          return "Evento";
      }
    };

    watch(
      () => route.query,
      async () => {
        openedIndexes.value.clear();
        await loadStatements();
      },
      { deep: true }
    );

    watch(
      [selectedType, selectedTransaction, firstDate, lastDate],
      () => {
        router.replace({ query: { ...route.query, page: 1 } });
      }
    );

    const isClickable = (statement: Statement) =>
      [StatementType.COURSE, StatementType.PRODUCT].includes(statement.type);

    const toggleStatement = (index: number) => {
      if (openedIndexes.value.has(index)) {
        openedIndexes.value.delete(index);
      } else {
        openedIndexes.value.add(index);
      }
      openedIndexes.value = new Set(openedIndexes.value);
    };

    const getStatementClassMap = (statement: Statement) => {
      return {
        debit: statement.transactionType === TransactionType.DEBIT,
        credit: statement.transactionType === TransactionType.CREDIT,
        canceled: statement.typeCanceled === StatementTypeCanceled.CANCELED
      };
    };

    return {
      user,
      formattedTransaction,
      contentTypes,
      selectedType,
      openedIndexes,
      formatDecoins,
      formatName,
      formatStatementType,
      StatementTypeCanceled,
      isClickable,
      toggleStatement,
      numberOfPages,
      StatementType,
      transactionTypes,
      statements,
      getStatementClassMap,
      selectedTransaction,
      firstDate,
      lastDate
    };
  }
});
